import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { logger } from '@/lib/utils/logger';

export function AuthCallback() {
  const navigate = useNavigate();
  const { user, isLoading } = useUnifiedAuth();
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    // The unified auth service handles OAuth callback internally
    // We just need to wait for the auth state to update
    if (!isLoading) {
      if (user) {
        logger.debug('OAuth authentication successful');
        // Get the stored redirect path or default to home
        const redirectPath = window.sessionStorage.getItem('authRedirectPath') || '/';
        window.sessionStorage.removeItem('authRedirectPath'); // Clean up
        navigate(redirectPath, { replace: true });
      } else {
        // Check if there's an error in the URL params
        const params = new URLSearchParams(window.location.search);
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');
        
        if (errorParam) {
          logger.error('OAuth error:', errorParam, errorDescription);
          setError(errorDescription || errorParam || 'Authentication failed');
          // Redirect to home after showing error
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
        } else {
          // No user and no error, likely still loading
          logger.debug('Waiting for auth state...');
        }
      }
    }
  }, [navigate, user, isLoading]);

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-500 mb-4">{error}</p>
            <p className="text-sand-400">Redirecting you back home...</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-sand-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sand-400">Completing authentication...</p>
          </>
        )}
      </div>
    </div>
  );
}